import { Link } from "react-router-dom";
import {
  Accessibility,
  Armchair,
  ArrowLeftRight,
  BadgeCheck,
  Battery,
  Briefcase,
  Building2,
  Calendar,
  Check,
  Clock,
  Eye,
  FileText,
  Flower2,
  HeartHandshake,
  Lock,
  MessageCircle,
  Package,
  PawPrint,
  Phone,
  PlaneTakeoff,
  Ruler,
  Shield,
  ShieldCheck,
  Tag,
  Users,
  UtensilsCrossed,
  Weight,
  Wrench,
  Zap,
} from "lucide-react";
import { Logo } from "@/components/Logo";

const TEAL_TEXT = "text-[#0B8A7B]";

const SHARING_META = [
  { Icon: Calendar, label: "Shared on", value: "31 Aug 2026", testid: "shared-on" },
  {
    Icon: Building2,
    label: "Shared with",
    value: "Example Airlines",
    verified: true,
    testid: "shared-with",
  },
  { Icon: Clock, label: "Access expires", value: "7 Sept 2026", sub: "at 11:59 PM", testid: "access-expires" },
  { Icon: Briefcase, label: "Shared Journey", value: "Travel (Air Travel)", testid: "shared-journey" },
];

const CATEGORIES = [
  { Icon: Accessibility, label: "Mobility" },
  { Icon: MessageCircle, label: "Communication" },
  { Icon: Eye, label: "Visual" },
];

const MOBILITY_NEEDS = [
  {
    Icon: ArrowLeftRight,
    title: "Transfer Needs",
    items: [
      "Requires a hoist to transfer onto the aircraft aisle seat.",
      "Cannot transfer independently.",
      "Requires trained assistance for all transfers.",
    ],
  },
  {
    Icon: Armchair,
    title: "Seating Preferences",
    items: [
      "Requires an aisle seat.",
      "Prefers to be seated close to the toilet.",
      "Requires sufficient space to safely transfer.",
    ],
  },
  {
    Icon: Users,
    title: "Travels With",
    items: [
      "Travels with a carer.",
      "Carer provides physical assistance during travel.",
      "Carer should be seated nearby where possible.",
    ],
  },
  {
    Icon: PlaneTakeoff,
    title: "Boarding & Disembarking",
    items: [
      "Requires additional time to board and disembark.",
      "Cannot use stairs.",
      "Requires level/accessible boarding where available.",
    ],
  },
];

const DEVICE_SPECS = [
  { Icon: Accessibility, label: "Type", value: "Power Wheelchair" },
  { Icon: Tag, label: "Make / Model", value: "Permobil F5 Corpus" },
  { Icon: Weight, label: "Weight (with battery)", value: "182 kg" },
  { Icon: Ruler, label: "Dimensions (L × W × H)", value: "120 cm × 66 cm × 128 cm" },
  { Icon: Battery, label: "Battery Type", value: "Lithium-ion" },
  { Icon: Zap, label: "Battery Capacity", value: "24V | 73Ah" },
  { Icon: Wrench, label: "Disassembly", value: "Does not disassemble" },
  { Icon: Package, label: "Other Equipment", value: "Headrest | Chest strap | ROHO High Profile cushion" },
];

const COMMUNICATION = [
  "Prefers clear, simple verbal communication.",
  "Allow additional time to process information.",
  "Written information is helpful.",
  "Please face me when speaking.",
  "Uses a smartphone to communicate if needed.",
];

const VISUAL = [
  "Prefers to be seated close to the toilet.",
  "Needs good lighting to read information.",
  "Benefits from clear visual signage.",
  "Prefers high-contrast written information.",
];

const OTHER_INFO = [
  { Icon: PawPrint, label: "Service Animal", value: "No" },
  { Icon: Flower2, label: "Allergies", value: "None" },
  { Icon: UtensilsCrossed, label: "Dietary Requirements", value: "Low salt" },
  { Icon: Phone, label: "Emergency Contact", value: "Carer – Sam Smith" },
  { Icon: FileText, label: "Additional Notes", value: "Please contact my carer for coordination upon arrival." },
];

const PRINCIPLES = [
  {
    Icon: Lock,
    title: "Shared by choice",
    body: "Jordan controls what is shared, with whom, and for how long. This information is only for the Travel (Air Travel) Journey and cannot be used for any other purpose.",
    testid: "principle-shared-by-choice",
  },
  {
    Icon: ShieldCheck,
    title: "Private. Secure. In control.",
    body: "A-ID sharing is consented, controlled and revocable at any time.",
    link: true,
    testid: "principle-private-secure",
  },
  {
    Icon: HeartHandshake,
    title: "Respect. Understand. Support.",
    body: "This information helps organisations provide a safe, respectful and inclusive experience.",
    testid: "principle-respect",
  },
];

const Card = ({ Icon, title, children, testid }) => (
  <section
    className="rounded-2xl border border-navy-800/10 bg-white p-5 sm:p-6 shadow-[0_2px_18px_-8px_rgba(10,19,34,0.18)]"
    data-testid={testid}
  >
    <header className="flex items-center gap-3">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-navy-800">
        <Icon className="h-5 w-5 text-teal" aria-hidden="true" />
      </span>
      <h3 className="font-display text-sm font-bold uppercase tracking-[0.12em] text-navy-800">{title}</h3>
    </header>
    <div className="mt-5">{children}</div>
  </section>
);

const BulletList = ({ items }) => (
  <ul className="mt-1.5 space-y-1.5">
    {items.map((item) => (
      <li key={item} className="flex gap-2 text-sm leading-relaxed text-slate-600">
        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-teal" aria-hidden="true" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const CheckList = ({ items }) => (
  <ul className="space-y-2.5">
    {items.map((item) => (
      <li key={item} className="flex items-start gap-2.5 text-sm leading-relaxed text-slate-600">
        <span className="mt-0.5 flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded-full bg-teal/15">
          <Check className="h-3 w-3 text-[#0B8A7B]" strokeWidth={3} aria-hidden="true" />
        </span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const ExampleAid = () => (
  <main className="min-h-screen bg-[#F3F8F9] font-sans text-navy-800" data-testid="example-aid-page">
    {/* Header */}
    <header className="border-b border-navy-800/10 bg-white" data-testid="example-aid-header">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
        <div>
          <Link to="/" aria-label="Back to the Aiga Enable website" data-testid="example-aid-home-link">
            <Logo imgClass="h-11" />
          </Link>
          <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.22em] text-[#0B8A7B] sm:text-[10px]">
            One identity. Many journeys. Your control.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="hidden items-start gap-2 sm:flex" data-testid="demo-data-note">
            <Shield className="mt-0.5 h-8 w-8 text-navy-800" strokeWidth={1.5} aria-hidden="true" />
            <p className="text-xs leading-snug text-slate-600">
              <span className="block font-semibold text-navy-800">This is a demo A-ID</span>
              Not real data. Example only.
            </p>
          </div>
          <span
            className="rounded-lg bg-navy-800 px-4 py-1.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white"
            data-testid="demo-badge"
          >
            Demo
          </span>
        </div>
      </div>
    </header>

    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      {/* Hero */}
      <section className="grid items-center gap-8 py-10 sm:py-14 lg:grid-cols-12" data-testid="example-aid-hero">
        <div className="lg:col-span-4">
          <p className="font-mono text-xs font-bold uppercase tracking-[0.24em] text-[#0B8A7B]">Example A-ID</p>
          <h1 className="mt-3 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-navy-800 sm:text-5xl">
            Here&rsquo;s an example of a <span className="text-[#0B8A7B]">shared&nbsp;A-ID</span>
          </h1>
          <p className="mt-4 max-w-md text-base leading-relaxed text-slate-600">
            This is how someone can securely share precise accessibility information with an organisation.
          </p>
        </div>

        {/* Shield + lock visual */}
        <div className="flex justify-center lg:col-span-3" aria-hidden="true">
          <div className="relative flex h-44 w-44 items-center justify-center sm:h-52 sm:w-52">
            <div className="absolute inset-0 rounded-full border-2 border-dashed border-teal/40 animate-spin-slower" />
            <div className="absolute inset-5 rounded-full bg-teal/10" />
            <Shield className="h-28 w-28 text-[#0B8A7B] sm:h-32 sm:w-32" strokeWidth={1.1} fill="rgba(0,210,180,0.12)" />
            <span className="absolute flex h-12 w-12 items-center justify-center rounded-full bg-navy-800 shadow-lg">
              <Lock className="h-5 w-5 text-teal" aria-hidden="true" />
            </span>
          </div>
        </div>

        {/* Sharing meta grid */}
        <div className="lg:col-span-5">
          <div
            className="grid grid-cols-1 overflow-hidden rounded-2xl border border-navy-800/10 bg-white shadow-[0_2px_18px_-8px_rgba(10,19,34,0.18)] sm:grid-cols-2"
            data-testid="sharing-meta-card"
          >
            {SHARING_META.map(({ Icon, label, value, sub, verified, testid }, i) => (
              <div
                key={label}
                className={`flex items-start gap-3 p-4 sm:p-5 ${i % 2 === 0 ? "sm:border-r" : ""} ${i < 2 ? "border-b" : ""} border-navy-800/10`}
                data-testid={testid}
              >
                <Icon className="mt-0.5 h-7 w-7 shrink-0 text-navy-800" strokeWidth={1.4} aria-hidden="true" />
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
                  <p className="mt-0.5 text-sm font-bold text-navy-800">{value}</p>
                  {sub && <p className="text-xs text-slate-500">{sub}</p>}
                  {verified && (
                    <p className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[#0B8A7B]">
                      <BadgeCheck className="h-4 w-4" aria-hidden="true" /> Verified Organisation
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
          <p className="mt-3 flex items-start gap-1.5 text-xs leading-snug text-slate-500" data-testid="limited-share-note">
            <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#0B8A7B]" aria-hidden="true" />
            A limited, Journey-specific share. Example Airlines does not receive Jordan&rsquo;s entire A-ID — only what
            Jordan chose to share for this journey.
          </p>
        </div>
      </section>

      {/* Demo profile band */}
      <section
        className="overflow-hidden rounded-3xl bg-gradient-to-br from-navy-700 to-navy-800 p-6 sm:p-8"
        data-testid="demo-profile-band"
      >
        <div className="grid items-center gap-8 lg:grid-cols-[1fr_auto]">
          <div>
            <div className="flex flex-wrap items-center gap-5">
              <span
                className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-teal font-display text-3xl font-extrabold text-navy-800 ring-4 ring-teal/25 sm:h-24 sm:w-24"
                aria-hidden="true"
              >
                JS
              </span>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2.5">
                  <h2 className="font-display text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
                    Jordan Smith
                  </h2>
                  <span className="rounded-md border border-cyan/40 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-cyan">
                    Demo profile
                  </span>
                </div>
                <p
                  className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-teal/15 px-2.5 py-1 font-mono text-xs text-teal"
                  data-testid="demo-aid-number"
                >
                  <BadgeCheck className="h-3.5 w-3.5" aria-hidden="true" /> A-ID: AID-DEMO-7824
                </p>
                <p className="mt-2.5 text-sm text-slate-300">This information is shared by Jordan.</p>
              </div>
            </div>
            <ul className="mt-6 flex flex-wrap gap-2.5" data-testid="demo-categories">
              {CATEGORIES.map(({ Icon, label }) => (
                <li
                  key={label}
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-sm text-slate-200"
                >
                  <Icon className="h-4 w-4 text-teal" aria-hidden="true" /> {label}
                </li>
              ))}
            </ul>
          </div>
          <div className="flex items-center gap-4 border-t border-white/10 pt-6 lg:flex-col lg:border-l lg:border-t-0 lg:pl-10 lg:pt-0">
            <Logo imgClass="h-10" />
            <p className="inline-flex items-center gap-1.5 font-display text-base font-bold text-white">
              A-ID Verified <BadgeCheck className="h-5 w-5 text-teal" aria-hidden="true" />
            </p>
          </div>
        </div>
      </section>

      {/* Detailed accessibility information */}
      <section className="py-10 sm:py-12" data-testid="detailed-info-section">
        <h2 className="font-display text-lg font-extrabold uppercase tracking-[0.12em] text-[#0B8A7B]">
          Detailed accessibility information
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-slate-600 sm:text-base">
          Jordan has chosen to share the following information for their Travel (Air Travel) journey.
        </p>

        <div className="mt-6 grid items-start gap-5 lg:grid-cols-3">
          {/* Mobility needs */}
          <Card Icon={Accessibility} title="Mobility Needs" testid="mobility-needs-card">
            <div className="space-y-5">
              {MOBILITY_NEEDS.map(({ Icon, title, items }, i) => (
                <div key={title} className={i > 0 ? "border-t border-navy-800/10 pt-5" : ""}>
                  <div className="flex items-start gap-3">
                    <Icon className="mt-0.5 h-5 w-5 shrink-0 text-[#0B8A7B]" strokeWidth={1.6} aria-hidden="true" />
                    <div>
                      <h4 className="text-sm font-bold text-navy-800">{title}</h4>
                      <BulletList items={items} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Wheelchair / mobility device */}
          <Card Icon={Accessibility} title="Wheelchair / Mobility Device" testid="wheelchair-device-card">
            <dl className="divide-y divide-navy-800/10">
              {DEVICE_SPECS.map(({ Icon, label, value }) => (
                <div key={label} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-teal/10">
                    <Icon className="h-4.5 w-4.5 text-[#0B8A7B]" strokeWidth={1.6} aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <dt className="font-mono text-[10px] uppercase tracking-[0.14em] text-slate-500">{label}</dt>
                    <dd className="mt-0.5 text-sm font-semibold text-navy-800">{value}</dd>
                  </div>
                </div>
              ))}
            </dl>
          </Card>

          {/* Communication / Visual / Other */}
          <div className="space-y-5">
            <Card Icon={MessageCircle} title="Communication" testid="communication-card">
              <CheckList items={COMMUNICATION} />
            </Card>
            <Card Icon={Eye} title="Visual" testid="visual-card">
              <CheckList items={VISUAL} />
            </Card>
            <Card Icon={FileText} title="Other Important Information" testid="other-info-card">
              <dl className="space-y-3.5">
                {OTHER_INFO.map(({ Icon, label, value }) => (
                  <div key={label} className="flex items-start gap-3">
                    <Icon className="mt-0.5 h-4.5 w-4.5 shrink-0 text-[#0B8A7B]" strokeWidth={1.6} aria-hidden="true" />
                    <dt className="w-32 shrink-0 text-sm font-semibold text-navy-800">{label}</dt>
                    <dd className="min-w-0 flex-1 text-sm leading-relaxed text-slate-600">{value}</dd>
                  </div>
                ))}
              </dl>
            </Card>
          </div>
        </div>
      </section>

      {/* Privacy & consent principles */}
      <section className="grid gap-5 pb-10 md:grid-cols-3" data-testid="privacy-principles">
        {PRINCIPLES.map(({ Icon, title, body, link, testid }) => (
          <article
            key={title}
            className="rounded-2xl border border-navy-800/10 bg-white p-6 shadow-[0_2px_18px_-8px_rgba(10,19,34,0.18)]"
            data-testid={testid}
          >
            <Icon className="h-9 w-9 text-navy-800" strokeWidth={1.3} aria-hidden="true" />
            <h3 className="mt-4 font-display text-sm font-extrabold uppercase tracking-[0.1em] text-navy-800">
              {title}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{body}</p>
            {link && (
              <p className="mt-3 text-sm font-semibold">
                <Link to="/" className={`${TEAL_TEXT} underline-offset-4 hover:underline`} data-testid="learn-more-link">
                  Learn more about A-ID
                </Link>
                <span className="ml-2 text-slate-500">aigaenable.co.nz</span>
              </p>
            )}
          </article>
        ))}
      </section>

      {/* Demo safety footer */}
      <footer className="border-t border-navy-800/10 pb-10 pt-6 text-center" data-testid="example-aid-footer">
        <p className="inline-flex items-center gap-2 text-xs text-slate-500">
          <ShieldCheck className="h-4 w-4 text-[#0B8A7B]" aria-hidden="true" />
          A-ID protects privacy while ensuring people get the support they need.
        </p>
        <p className="mt-2 text-xs text-slate-500">
          This is a demo A-ID — fictional demonstration information only. Not real data.
        </p>
      </footer>
    </div>
  </main>
);

export default ExampleAid;
